import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAdmin } from '../util/IsLoggin';
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  BarChart3,
  Settings,
  Wrench,
  CircleDollarSign,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const menuItems = [
  { key: '/dashboard', label: 'Thống Kê', icon: LayoutDashboard },
  { key: '/orders', label: 'Đơn Hàng', icon: FileText },
  { key: '/chat', label: 'Hội thoại', icon: MessageSquare },
  { key: '/products', label: 'Sản Phẩm', icon: ShoppingCart },
  { key: '/doisoat', label: 'Đối Soát', icon: CircleDollarSign },
  { key: '/reports', label: 'Báo Cáo', icon: BarChart3 },
  { key: '/setting', label: 'Cấu Hình', icon: Settings },
];

const Sidebar = ({ auth, collapsed, onCollapse, mobileOpen, onMobileOpenChange }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClick = (key) => {
    if (location.pathname !== key) {
      navigate(key);
    }
    onCollapse(true);
    if (onMobileOpenChange) onMobileOpenChange(false);
  };

  const renderMenuContent = (showLabels = false) => (
    <div className="flex flex-col h-full bg-[#0F172A] text-slate-200">
      {/* Logo / Avatar area */}
      <div className={cn(
        'flex items-center border-b border-white/[0.08]',
        showLabels ? 'py-4 px-4 gap-3 justify-start' : 'justify-center py-5 px-3'
      )}>
        <Avatar className="h-10 w-10 ring-2 ring-blue-500/40 shadow-sm shrink-0">
          <AvatarImage src="/pngegg.png" alt="logo" />
          <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white text-xs font-bold">BP</AvatarFallback>
        </Avatar>
        {auth.isAuthenticated && !auth.loading && auth.user && (showLabels || !collapsed) && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-semibold text-white truncate">
              {auth.user.phone || 'Tài khoản'}
            </span>
            <span className="text-[11px] text-blue-400 font-medium truncate">
              {auth.user.partition || 'BabyPony CRM'}
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.key || location.pathname.startsWith(item.key + '/');
          return (
            <Button
              key={item.key}
              variant="ghost"
              onClick={() => handleMenuClick(item.key)}
              className={cn(
                'w-full justify-start rounded-lg transition-all duration-150',
                showLabels ? 'h-11 text-sm px-3' : 'h-10 text-[13px]',
                'text-slate-400 hover:text-white hover:bg-white/[0.08]',
                isActive && 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/25 hover:text-blue-300 font-semibold border-l-2 border-blue-500 rounded-l-none'
              )}
            >
              <item.icon className={cn('h-5 w-5 shrink-0', (showLabels || !collapsed) && 'mr-3')} />
              {(showLabels || !collapsed) && <span className="font-medium truncate">{item.label}</span>}
            </Button>
          );
        })}
        {isAdmin() && (
          <Button
            key="/admin"
            variant="ghost"
            onClick={() => handleMenuClick('/admin')}
            className={cn(
              'w-full justify-start rounded-lg transition-all duration-150',
              showLabels ? 'h-11 text-sm px-3' : 'h-10 text-[13px]',
              'text-slate-400 hover:text-white hover:bg-white/[0.08]',
              location.pathname === '/admin' && 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/25 hover:text-blue-300 font-semibold border-l-2 border-blue-500 rounded-l-none'
            )}
          >
            <Wrench className={cn('h-5 w-5 shrink-0', (showLabels || !collapsed) && 'mr-3')} />
            {(showLabels || !collapsed) && <span className="font-medium truncate">Admin</span>}
          </Button>
        )}
      </nav>

      {showLabels && (
        <div className="p-3 border-t border-white/[0.08] text-[11px] text-slate-500 text-center pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          CRM v0.1.0 • BabyPony
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="bg-[#0F172A] p-0 w-72 max-w-[85vw] border-r border-slate-800 text-white">
          {renderMenuContent(true)}
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col bg-[#0F172A] text-white transition-all duration-200 ease-in-out border-r border-white/[0.06]',
          collapsed ? 'w-16' : 'w-48'
        )}
      >
        {renderMenuContent()}
      </aside>
    </>
  );
};

Sidebar.propTypes = {
  auth: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func.isRequired,
  mobileOpen: PropTypes.bool,
  onMobileOpenChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  auth: state.AuthRedux,
});

export default connect(mapStateToProps)(Sidebar);
